<template>
  <div class="mb-4">
    <label
      v-if="label"
      :class="clsx(
        ['select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium'],
      )"
    >
      {{ label }}
    </label>

    <span
      :class="clsx(
        [
          'relative block w-full',
          'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:shadow',
          // Focus ring
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:after:focus-within:ring-2 sm:after:focus-within:ring-primary-700',
          // Disabled state
          'has-[[disabled]]:opacity-50 before:has-[[disabled]]:bg-slate-950/5 before:has-[[disabled]]:shadow-none',
          'before:border',
          error ? 'before:border-error before:hover:border-error' : 'before:border-slate-950/10 before:hover:border-slate-950/20'
        ],
        label && 'mt-2'
      )"
    >
      <div class="flex flex-wrap gap-2 relative z-50">
        <div class="flex flex-wrap gap-1 ml-1">
          <div
            v-for="(tag, index) in tags" :key="tag"
            :class="[
              'flex items-center gap-1 bg-secondary text-slate-950 rounded-lg px-2 py-1',
              'my-1',
            ]"
          >
            <span>{{ tag }}</span>
            <button
              type="button"
              class="text-sm font-bold"
              @click="removeTag(index, tag)"
            >
              &times;
            </button>
          </div>
        </div>
        <input
          v-model="input"
          :placeholder="placeholder"
          :type="type"
          v-bind="$attrs"
          :class= "[
              // Expansion
              'flex-grow',
              // Basic layout
              'relative appearance-none rounded-lg px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing[3])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
              // Typography
              'text-base/6 text-slate-950 placeholder:text-slate-500 sm:text-sm/6',
              // Border
              'border-none',
              // Background color
              'bg-transparent',
              // Hide default focus styles
              'focus:outline-none focus:shadow-none focus:ring-0',
              // Disabled state
              '[disabled]:border-slate-950/20',
            ]"
          @keydown="handleKeydown"
          @blur="addTag"
        />
      </div>
    </span>

    <p
      v-if="description"
      :class="clsx(
        ['mt-2 text-base/6 text-slate-500 sm:text-sm/6'],
        $attrs.disabled && 'opacity-50'
      )"
    >
      {{ description }}
    </p>

    <p
      v-if="error && !noErrorMessages"
      :class="clsx(
        ['text-base/6 text-error sm:text-sm/6'],
        $attrs.disabled && 'opacity-50',
        !description && 'mt-2'
      )"
    >
      {{ error }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue'
import clsx from 'clsx'

const model = defineModel<string[]>()

defineProps({
  placeholder: {
    type: String,
    default: 'Add a tag',
  },
  label: {
    type: String,
    default: ''
  },
  type: {
    type: String as PropType<'text' | 'number' | 'email'>,
    default: 'text'
  },
  description: {
    type: String,
    default: ''
  },
  error: {
    type: String as PropType<any>,
    default: ''
  },
  noErrorMessages: {
    type: Boolean,
    default: false
  }
})

const input = ref('')
const tags = ref<string[]>([...model.value])

const addTag = () => {
  const values = input.value
    .split(/[ ,;]/) // split tags by space, comma or semicolon
    .map((text) => text.trim())
    .filter((text) => text && !tags.value.some((tag) => tag === text))

  tags.value = [...tags.value, ...values]
  model.value = tags.value
  input.value = ''
}

const removeTag = (index: number, tag: string) => {
  tags.value = tags.value.filter((val, idx) => val !== tag && idx !== index)
  model.value = tags.value
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Backspace' && !input.value && tags.value.length > 0) {
    // Remove last tag when pressing backspace and input is empty
    removeTag(tags.value.length - 1, tags.value[tags.value.length - 1])
  } else if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
    event.preventDefault()
    addTag()
  }
}
</script>
